import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useCreateMfeSessionMutation, useLazyGetCoreApplicationContextQuery } from "@webcore/services/api/api.ts";
import { FullViewSpinner } from "@webcore/components/core/FullViewSpinner/FullViewSpinner.tsx";
import { CoreApplicationContextProvider } from "@webcore/context-providers/CoreApplicationContextProvider.tsx";
import { OpsAppUserContext } from "@webcore/models/coreModels.ts";
import { dispatchMfeMessage, MfeMessageHandlers, MfeReadyMessage, mfeReadyMessageType, mfeTokenMessageType } from "@webcore/services/mfe/mfeMessaging.ts";
import { adoptClientInstanceId } from "@webcore/services/clientInstanceId.ts";

/**
 * The session MfeBootstrap established: the user context the token exchange authorized. Anything
 * rendered under the bootstrap can read it, so hosts of the ops app platform (the view routes)
 * provide it without a further request.
 */
export interface MfeSession {
  userContext: OpsAppUserContext;
}

type BootstrapState =
  | { status: "awaiting-token" | "establishing" | "error" }
  | { status: "ready"; session: MfeSession };

const MfeSessionContext = createContext<MfeSession | undefined>(undefined);

export const useMfeSession = (): MfeSession => {
  const context = useContext(MfeSessionContext);

  if (context === undefined) {
    throw new Error("useMfeSession must be used within MfeBootstrap");
  }

  return context;
};

/**
 * Reads a one-time token from the URL fragment (e.g. `#mfe_token=...`) if the parent chose
 * the fragment-handoff path rather than postMessage. The fragment is cleared immediately so
 * the token is not left in history.
 */
function consumeTokenFromFragment(): string | null {
  const hash = window.location.hash.replace(/^#/, "");

  if (hash === "") {
    return null;
  }

  const params = new URLSearchParams(hash);
  const token = params.get("mfe_token");

  if (token === null) {
    return null;
  }

  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  return token;
}

/**
 * MFE bootstrap. Receives the short-lived read-only token from the parent app (postMessage,
 * with a URL-fragment fallback), POSTs it to `/api/v1/auth/mfe-session` to set a host-only
 * read-only cookie on this origin, then loads the CoreApplicationContext for that session and
 * renders its children under the shared CoreApplicationContextProvider — the same way web-client's
 * auth layer does, just at read scope. The token exchange also returns the user context the new
 * session runs under, which the bootstrap exposes as the MfeSession. This is the MFE's "auth"
 * layer: nothing below it renders until a session cookie exists, its user context is known, and a
 * CoreApplicationContext (with the active team) is loaded.
 */
function MfeBootstrap({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BootstrapState>({ status: "awaiting-token" });
  const [createMfeSession] = useCreateMfeSessionMutation();
  const [getCoreApplicationContext] = useLazyGetCoreApplicationContextQuery();

  useEffect(() => {
    let isActive = true;

    const establishSession = async (token: string) => {
      setState({ status: "establishing" });

      try {
        const { userContext } = await createMfeSession({ token }).unwrap();
        // The read cookie is now set; load the CoreApplicationContext into the coreContext slice so
        // the shared provider can expose it (and the active team) to the rendered view.
        await getCoreApplicationContext().unwrap();

        if (isActive) {
          setState({ status: "ready", session: { userContext } });
        }
      }
      catch {
        if (isActive) {
          setState({ status: "error" });
        }
      }
    };

    const handlers: MfeMessageHandlers = {
      [mfeTokenMessageType]: (message) => {
        window.removeEventListener("message", window_message);

        // Adopt the parent's data-change echo-suppression id before the first API call, so writes
        // made by either the parent app or this iframe are suppressed as echoes in both.
        if (message.clientInstanceId !== undefined) {
          adoptClientInstanceId(message.clientInstanceId);
        }

        void establishSession(message.token);
      },
    };

    const window_message = (event: MessageEvent) => {
      // Only accept messages from the parent (app) window. The token itself is validated
      // server-side, so we don't pin a single parent origin.
      if (event.source !== window.parent) {
        return;
      }

      dispatchMfeMessage(event, handlers);
    };

    const fragmentToken = consumeTokenFromFragment();

    if (fragmentToken !== null) {
      void establishSession(fragmentToken);
    }
    else {
      window.addEventListener("message", window_message);
      // Tell the parent we are mounted and ready to receive the token.
      const readyMessage: MfeReadyMessage = { type: mfeReadyMessageType };
      window.parent.postMessage(readyMessage, "*");
    }

    return () => {
      isActive = false;
      window.removeEventListener("message", window_message);
    };
  }, [createMfeSession, getCoreApplicationContext]);

  if (state.status === "error") {
    return (
      <div className="p-4">
        <p className="text-danger">Could not establish a read-only session.</p>
      </div>
    );
  }

  if (state.status !== "ready") {
    return <FullViewSpinner />;
  }

  // The session is established and the CoreApplicationContext is loaded; expose both to the
  // rendered subtree.
  return (
    <MfeSessionContext.Provider value={state.session}>
      <CoreApplicationContextProvider>{children}</CoreApplicationContextProvider>
    </MfeSessionContext.Provider>
  );
}

export default MfeBootstrap;
